import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;
const PAD = 10;
const DEFAULT_DELAY = 220;

function computePos(anchorRect, tipW, tipH, placement) {
  const centerX = anchorRect.left + anchorRect.width / 2;
  let left = centerX - tipW / 2;
  left = Math.min(Math.max(left, PAD), window.innerWidth - tipW - PAD);

  let top;
  let place = placement;
  if (placement === 'top') {
    top = anchorRect.top - tipH - GAP;
    if (top < PAD) {
      place = 'bottom';
      top = anchorRect.bottom + GAP;
    }
  } else {
    top = anchorRect.bottom + GAP;
    if (top + tipH > window.innerHeight - PAD) {
      place = 'top';
      top = Math.max(PAD, anchorRect.top - tipH - GAP);
    }
  }

  const arrowLeft = Math.min(Math.max(centerX - left, 12), tipW - 12);
  return { top, left, arrowLeft, place };
}

/**
 * Tooltip moderno com seta (portal) para a gestão de reembolsos.
 * @param {string|React.ReactNode} content
 * @param {'top'|'bottom'} [placement]
 */
export function GrHoverTooltip({
  content,
  children,
  placement = 'top',
  delayMs = DEFAULT_DELAY,
  className = '',
  multiline = false,
}) {
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const timerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [side, setSide] = useState(placement);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const measure = useCallback(() => {
    const a = anchorRef.current;
    const t = tipRef.current;
    if (!a || !t) return;
    const ar = a.getBoundingClientRect();
    const tr = t.getBoundingClientRect();
    const pos = computePos(ar, tr.width, tr.height, placement);
    setCoords({ top: pos.top, left: pos.left, arrowLeft: pos.arrowLeft });
    setSide(pos.place);
  }, [placement]);

  const show = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delayMs);
  }, [clearTimer, delayMs]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setCoords(null);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure, content]);

  if (content == null || content === '') {
    return children;
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={['gr-tip-anchor', className].filter(Boolean).join(' ')}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <span
              ref={tipRef}
              className={[
                'gr-tip-bubble',
                `gr-tip-bubble--${side}`,
                multiline ? 'is-multiline' : '',
                coords ? 'is-ready' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                coords
                  ? {
                      top: coords.top,
                      left: coords.left,
                      '--gr-tip-arrow': `${coords.arrowLeft}px`,
                    }
                  : { top: -9999, left: -9999, opacity: 0 }
              }
              role="tooltip"
            >
              {content}
            </span>,
            document.body
          )
        : null}
    </>
  );
}
